import type { AxiosProgressEvent } from "axios";
import axios from "axios";
import type { Configuration } from "@freightsimple/generated-dashboard-openapi-client/runtime";
import type { OpenFreightClaimRequest } from "@freightsimple/generated-dashboard-openapi-client/models/OpenFreightClaimRequest";
import { FreightClaimsApi as OpenAPIFreightClaimsApi } from "@freightsimple/generated-dashboard-openapi-client/apis/FreightClaimsApi";

// 5 minutes — large multipart uploads need generous timeouts.
// CustomsDocsApi uses 120s for smaller payloads; claims can include
// up to 30 files so we allow more headroom.
const UPLOAD_TIMEOUT_MS = 300_000;
const MAX_RETRIES = 3;

function isRetryableError(e: unknown): boolean {
  if (!axios.isAxiosError(e)) return false;
  // Retry on network errors (no response received) and timeouts
  return !e.response || e.code === "ERR_NETWORK" || e.code === "ECONNABORTED";
}

export class FreightClaimsApi extends OpenAPIFreightClaimsApi {
  constructor(configuration: Configuration) {
    super(configuration);
  }

  async upload(
    request: OpenFreightClaimRequest,
    originalInvoiceFiles: Array<File>,
    photosFiles: Array<File>,
    otherDocumentsFiles: Array<File>,
    onUploadProgress?: (percentComplete: number) => void,
  ): Promise<void> {
    let tokenString: string | undefined;

    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      tokenString = await token("JWT", []);
    }

    if (!tokenString) {
      console.error("No authorization token");
      return;
    }

    const formData = new FormData();

    originalInvoiceFiles.forEach(function (file, index) {
      formData.append(`originalInvoiceFile${index}`, file, file.name);
    });

    photosFiles.forEach(function (file, index) {
      formData.append(`photosFile${index}`, file, file.name);
    });

    otherDocumentsFiles.forEach(function (file, index) {
      formData.append(`otherDocumentsFile${index}`, file, file.name);
    });

    const encodedRequest = encodeURIComponent(JSON.stringify(request));
    const url = `${this.configuration.basePath}/freight-claims/open?request=${encodedRequest}`;

    const options = {
      headers: {
        Authorization: `Bearer ${tokenString}`,
        "Content-Type": "multipart/form-data",
      },
      timeout: UPLOAD_TIMEOUT_MS,
      ...(onUploadProgress
        ? {
            onUploadProgress: (event: AxiosProgressEvent) => {
              if (event.total && event.total > 0) {
                onUploadProgress(
                  Math.round((event.loaded / event.total) * 100),
                );
              }
            },
          }
        : {}),
    };

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        await axios.post(url, formData, options);
        return;
      } catch (e) {
        const isLastAttempt = attempt === MAX_RETRIES;

        if (isLastAttempt || !isRetryableError(e)) {
          throw e;
        }

        console.warn(
          `Freight claim upload attempt ${attempt} failed, retrying...`,
          axios.isAxiosError(e)
            ? { code: e.code, message: e.message }
            : undefined,
        );

        // Exponential backoff: 2s, 4s
        await new Promise((resolve) =>
          setTimeout(resolve, 2000 * Math.pow(2, attempt - 1)),
        );

        // Reset progress for retry
        onUploadProgress?.(0);
      }
    }
  }
}
